import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useAlert } from 'react-alert';
import { useNavigate } from 'react-router';
import useAuthz from '../../hooks/useAuthz';
import axios from 'axios';
import { URL_API } from '../../utils/globalConfig';
import { logError } from '../../components/Utils/FormatData';
import moment from 'moment';
import { PLANIFICATION_STATUS } from '../../constants/enum';
import TopBar from '../../components/Layout/TopBar';
import MyButton from '../../components/Layout/MyButton';
import { faCaretLeft, faCaretRight, faPlus } from '@fortawesome/free-solid-svg-icons';
import MySelect from '../../components/Layout/MySelect';
import SelectorTypeList from '../../components/AgGrid/SelectorTypeListe';
import ClipLoader from 'react-spinners/ClipLoader';
import FullCalendar from '@fullcalendar/react';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import frLocale from '@fullcalendar/core/locales/fr';
import AgGrid from '../../components/AgGrid/AgGrid';
import EditPlanificationModal from '../../components/Planification/EditPlanificationModal';
import PlanificationStatusRenderer from '../../components/Planification/PlanificationStatusRenderer';
import DossierIncompleteRenderer from '../../components/AgGrid/DossierIncompleteRenderer';
import ActionsPlanificationRenderer from '../../components/AgGrid/ActionsPlanificationRenderer';
import PlanificationEvent from '../../components/Calendar/PlanificationEvent';
import statusRenderer from '../../components/AgGrid/StatusRenderer';
import { DatePicker, DateTimePicker } from '@mui/x-date-pickers';
import { TextField } from '@mui/material';
import UnavailablePeriodModal from '../../components/Calendar/UnavailablePeriodModal';

export const PLANIFICATION_TYPE = { PLANNING: 'planning', LIST: 'liste' };
const currentPage = 'planification';
const calendarRef = React.createRef();

const statusList = [
    { label: 'Nouveau', value: PLANIFICATION_STATUS.NEW },
    { label: 'Nouveau', value: PLANIFICATION_STATUS.AVAILABLE },
    { label: 'Accepté prévisionnel', value: PLANIFICATION_STATUS.ACCEPTED_PROVISIONAL },
    { label: 'Accepté centre', value: PLANIFICATION_STATUS.ACCEPTED_CENTER },
    { label: 'Confirmé client', value: PLANIFICATION_STATUS.CONFIRMED_CLIENT },
    { label: 'Confirmé centres', value: PLANIFICATION_STATUS.CONFIRMED_CENTERS },
    { label: 'Accord émis', value: PLANIFICATION_STATUS.AGREEMENT_ISSUED },
    { label: 'Passage effectué', value: PLANIFICATION_STATUS.PASSAGE_MADE },
    { label: 'Non effectué', value: PLANIFICATION_STATUS.NOT_DONE },
    { label: 'Annulé', value: PLANIFICATION_STATUS.CANCELLED },
    { label: 'Refusé', value: PLANIFICATION_STATUS.REFUSED },
];

const PlanificationViewer = (props) => {
    const alert = useAlert();
    const navigate = useNavigate();
    const [authz, user, profil] = useAuthz();
    const requestPassId = new URLSearchParams(location.search).get('id') || null;
    const planificationId = new URLSearchParams(location.search).get('planificationId') || null;
    const client = new URLSearchParams(location.search).get('client') || null;
    const [selectedValue, setSelectedValue] = useState({ centerSelected: [] });
    const [pageFullyLoaded, setPageFullyLoaded] = useState(false);
    //#region useState

    const [isLoading, setIsLoading] = useState(true);
    const [centersAffected, setCentersAffected] = useState([]);
    const centersAffectedRef = useRef([]);
    const [listCenters, setListCenters] = useState([]);
    const listCentersRef = useRef([]);
    const [selectedCenter, setSelectedCenter] = useState(null);
    const [filters, setFilters] = useState(null);
    const [dateRange, setDateRange] = useState({
        startDate: moment().startOf('week').format(),
        endDate: moment().endOf('week').format(),
    });
    const [currentDate, setCurrentDate] = useState(moment().format());
    const [selectedView, setSelectedView] = useState('Semaine');
    const [planifications, setPlanifications] = useState([]);
    const [calendarEvents, setCalendarEvents] = useState([]);
    const [tab, setTab] = useState(PLANIFICATION_TYPE.PLANNING);
    const [reloadDatas, setReloadDatas] = useState(false);
    const [openModal, setOpenModal] = useState(false);
    const [planificationMode, setPlanificationMode] = useState(PLANIFICATION_STATUS.AVAILABLE);
    const [selectedPlanification, setSelectedPlanification] = useState(null);
    const [agGridOptions, setAgGridOptions] = useState({
        suppressCellSelection: true,
        suppressHorizontalScroll: false,
        animateRows: true,
    });
    const columns = useMemo(() => [
        { headerName: 'N° dossier', field: 'dossier_id', sortable: true, flex: 2, filter: 'agNumberColumnFilter' },
        { headerName: 'N° planification', field: 'id', sortable: true, flex: 2, filter: 'agNumberColumnFilter', hide: true },
        {
            headerName: 'N° Demande',
            field: 'dossier_id',
            sortable: true,
            flex: 2,
            valueGetter: (params) => {
                let dossier = params.data.dossier;
                let planification = params.data;

                if (planification.type === PLANIFICATION_STATUS.UNAVAILABLE) {
                    return '-';
                }

                const weight = (p) => p.metadata?.concernedPath?.full == true ? 1 : 0.5;

                const sorted = (dossier?.planifications ?? [])
                    .filter((p) => p.deleted === false)
                    .sort((a, b) => a.id - b.id);

                let n = 0;
                for (const p of sorted) {
                    n += weight(p);
                    if (p.id === planification.id) break;
                }

                const nbPlanification = dossier
                    ? (dossier.metadata.datePassage.nbPassageAller ?? 0) + (dossier.metadata.datePassage.nbPassageRetour ?? 0)
                    : 0;

                return `${n}/${nbPlanification}`;
            }
        },
        {
            headerName: 'Ref DDT',
            field: 'dossier_id',
            sortable: true,
            flex: 2,
            valueGetter: (params) => {
                let dossier = params.data.dossier;

                if (dossier) {
                    return `${dossier.metadata.reference.numDossier}`;
                }

                return '-';
            },
        },
        {
          headerName: 'Date de réception',
          field: 'date_creation',
          flex: 2,
          minWidth: 200,
          sortable: true,
          valueGetter: (params) => {
              return params.data.date_creation ? moment(params.data.date_creation).format('DD/MM/YYYY HH:mm') : '-'
          },
          filter: 'agDateColumnFilter',
        },
        {
            headerName: 'centres',
            field: 'metadata.centers',
            flex: 2.2,
            sortable: true,
            cellStyle: { 'justify-content': 'flex-start' },
            valueGetter: (params) => {
                let centers = params.data.metadata.centers ?? [];
                let centersNames = '';
                centers.map((centerId) => {
                    let center = listCentersRef.current.find((center) => Number(center.id) === Number(centerId.id));
                    if (center) {
                        centersNames += center.label + ', ';
                    }
                });

                centersNames = centersNames.slice(0, -2);

                return centersNames;
            }
        },
        {
            headerName: 'mandataire',
            field: 'metadata',
            flex: 2.2,
            sortable: true,
            valueGetter: (params) => {
                let dossier = params.data.dossier;

                if(dossier) {
                    return `${dossier.metadata.complementIdentite.mandataire.transporteur ?? '-'}`
                }

                return '-';
            }
        },
        {
            headerName: 'pétitionnaire',
            field: 'metadata',
            flex: 2.2,
            sortable: true,
            valueGetter: (params) => {
                let dossier = params.data.dossier;

                if(dossier) {
                    return `${dossier.metadata.complementIdentite.transporteur}`;
                }

                return '-';
            }
        },
        {
            headerName: 'type demande',
            field: 'type',
            flex: 2,
            sortable: true,
            valueGetter: (params) => {
                let dossier = params.data.dossier;
                let planificationId = params.data.id;
                let currentPlanification = dossier?.planifications.find((planification2) => {
                    return planification2.id === planificationId;
                });

                if (dossier) {
                    return currentPlanification.metadata.askType ? currentPlanification.metadata.askType === 'wanted' ? 'Souhaitée' : 'Optionnelle' : '-';
                }

                return '-';
            },
            filter: 'agTextColumnFilter',
        },
        {
            headerName: 'Disponibilité période',
            field: 'dispo',
            flex: 2,
            sortable: true,
            valueGetter: (params) => (params.data.type ? (params.data.type === PLANIFICATION_STATUS.AVAILABLE ? 'Disponible' : 'Indisponible') : '-'),
            filter: 'agTextColumnFilter',
        },
        {
            headerName: 'date de début',
            field: 'metadata.startDate',
            flex: 2,
            sortable: true,
            valueGetter: (params) => {
                if (params.data.metadata.newStartDate)
                    return moment(params.data.metadata.newStartDate).format('DD/MM/YYYY HH:mm');

                if (params.data.metadata.startDate)
                    return moment(params.data.metadata.startDate).format('DD/MM/YYYY HH:mm');

                return '-';
            },
            filter: 'agDateColumnFilter',
        },
        {
            headerName: 'date de fin',
            field: 'metadata.endDate',
            flex: 2,
            sortable: true,
            valueGetter: (params) => {
                if (params.data.metadata.newEndDate)
                    return moment(params.data.metadata.newEndDate).format('DD/MM/YYYY HH:mm');

                if (params.data.metadata.endDate)
                    return moment(params.data.metadata.endDate).format('DD/MM/YYYY HH:mm');

                return '-';
            },
            filter: 'agDateColumnFilter',
        },
        {
            headerName: 'motif',
            field: 'metadata.description',
            flex: 2.2,
            sortable: true,
            valueGetter: (params) => {
                let dossier = params.data.dossier;
                let planificationId = params.data.id;
                let currentPlanification = dossier?.planifications.find((planification2) => {
                    return planification2.id === planificationId;
                });

                if (dossier) {
                    return currentPlanification.metadata.additionalInfos.services ?? '-';
                } else {
                    if (params.data.type === 'UNAVAILABLE') {
                        return params.data.metadata.comment ?? '-';
                    }
                }

                return '-';
            },
        },
        {
            headerName: 'statut',
            field: 'metadata.statut',
            flex: 2.2,
            sortable: true,
            cellRenderer: 'planificationStatusRenderer',
            cellStyle: { 'justify-content': 'flex-start' },
            cellRendererParams: {
                displayIcon: true,
            },
            valueGetter: (params) => {
                let status = statusList.find((status) => status.value === params.data.metadata.status);
                return status ? status.label : '-';
            },
            filter: 'agTextColumnFilter',
            pinned: 'right',
        },{
            headerName: '',
            field: 'dossier.incomplete',
            flex: 0.6,
            minWidth: 30,
            maxWidth: 50,
            sortable: true,
            cellRenderer: 'dossierIncompleteRenderer',
            cellStyle: { 'justify-content': 'flex-start' },
            pinned: 'right',
        },
        {
            headerName: 'actions',
            field: '',
            sortable: false,
            cellStyle: { 'justify-content': 'flex-start' },
            cellRenderer: 'actionsPlanificationRenderer',
            cellRendererParams: {
                setReloadDatas: setReloadDatas,
                setSelectedPlanification: setSelectedPlanification,
                setOpenModal: setOpenModal,
                setPlanificationMode: setPlanificationMode
            },
            pinned: 'right',
        },
    ], []);
    const frameworkComponents = {
        planificationStatusRenderer: PlanificationStatusRenderer,
        dossierIncompleteRenderer: DossierIncompleteRenderer,
        actionsPlanificationRenderer: ActionsPlanificationRenderer,
    };

    //#endregion

    //#region useEffect

    useEffect(() => {
        if (profil.profil === 6) {
            alert.error(`Vous n'êtes pas autorisé à accéder au module de planification`);
            navigate('/');
        } else {
            document.title = 'TE - Planification';
            apiGetCentersAffectedToUser();
            apiGetListCenters();

            if (planificationId) {
                setTab(PLANIFICATION_TYPE.LIST);
                setFilters({ id: planificationId });
            }
        }
    }, []);
    useEffect(() => {
        if (dateRange || selectedCenter || reloadDatas) {
            if (calendarRef && calendarRef.current && calendarRef.current.getApi()) {
                calendarRef.current.getApi().changeView(selectedView === 'Semaine' ? 'timeGridWeek' : 'timeGridDay');
            }
            setIsLoading(true);
            setReloadDatas(false);
            apiGetPlanifications();
        }
    }, [dateRange, selectedCenter, tab, reloadDatas]);

    useEffect(() => {
        setIsLoading(true);

        apiGetPlanifications();

    }, [selectedValue, centersAffected]);

    //#endregion

    //#region Functions

    const convertPlanificationsToCalendarEvent = (planifications) => {
        let calendarEvents = [];
        let now = moment();

        planifications.map((planification) => {
            let metadata = planification.metadata;
            let startDate = moment(metadata.startDate);
            let endDate = moment(metadata.endDate);
            let isEditable = !startDate.isBefore(now, 'day') && planification.metadata.statut === PLANIFICATION_STATUS.AWAITING_VALIDATION;
            let noInteraction = planification.type === PLANIFICATION_STATUS.UNAVAILABLE && profil.profil > 2;

            calendarEvents.push({
                id: planification.id,
                title: metadata.client,
                start: startDate.format(),
                end: endDate.format(),
                editable: isEditable && !noInteraction,
                eventStartEditable: true,
                eventResizableFromStart: true,
                eventDurationEditable: true,
                extendedProps: {
                    planification: planification,
                },
                status: planification.metadata.status,
                askType: planification.metadata.askType,
            });
        });

        return calendarEvents;
    };
    const updateLocalStorageFilters = (values) => {
        if (pageFullyLoaded) {
            window.localStorage.setItem('filters', JSON.stringify({
                [currentPage]: values,
            }));
        }
    };
    const changeSelectValue = (e) => {
        setSelectedValue({ ...selectedValue, [e.target.name]: e.target.value });
        updateLocalStorageFilters({
            selectedValue: {
                [e.target.name]: e.target.value,
            },
        });
    };
    const onCreatePeriodModalBtnClick = (mode) => {
        setPlanificationMode(mode);
        setOpenModal(true);
    };
    const onPrevWeekBtnClick = () => {
        if (calendarRef && calendarRef.current && calendarRef.current.getApi()) {
             let calendarApi = calendarRef.current.getApi();

            if (selectedView === 'Jour') {
                let previousDay = moment(calendarApi.getDate()).subtract(1, 'days');
                let startDate = previousDay.startOf('day').format();
                let endDate = previousDay.endOf('day').format();

                setDateRange({
                    startDate: startDate,
                    endDate: endDate
                });

                calendarApi.prev();

                setCurrentDate(startDate);
            } else if (selectedView === 'Semaine') {
                let startDate = moment(calendarApi.getDate()).subtract(1, 'weeks').startOf('week');
                let endDate = moment(calendarApi.getDate()).subtract(1, 'weeks').endOf('week');

                setDateRange({
                    startDate: startDate.format(),
                    endDate: endDate.format(),
                });

                calendarApi.prev();

                setCurrentDate(startDate.format());
            }
        }
    }
    const onTodayBtnClick = () => {
        if (calendarRef && calendarRef.current && calendarRef.current.getApi()) {

            let calendarApi = calendarRef.current.getApi();

            if (selectedView === 'Jour') {
                let dayFormat = moment();
                let startDate = dayFormat.startOf('day').format();
                let endDate = dayFormat.endOf('day').format();

                setDateRange({
                    startDate: startDate,
                    endDate: endDate
                });

                setCurrentDate(startDate);
            } else if (selectedView === 'Semaine') {
                setDateRange({
                    startDate: moment().startOf('week').format(),
                    endDate: moment().endOf('week').format(),
                });

                setCurrentDate(moment().startOf('week').format());
            }

            calendarApi.today();
        }
    };
    const onNextWeekBtnClick = () => {
        if (calendarRef && calendarRef.current && calendarRef.current.getApi()) {

            let calendarApi = calendarRef.current.getApi();

            if (selectedView === 'Jour') {
                let nextDay = moment(calendarApi.getDate()).add(1, 'days');
                let startDate = nextDay.startOf('day').format();
                let endDate = nextDay.endOf('day').format();

                setDateRange({
                    startDate: startDate,
                    endDate: endDate
                });

                calendarApi.next();

                setCurrentDate(startDate);
            } else if (selectedView === 'Semaine') {
                let startDate = moment(calendarApi.getDate()).add(1, 'weeks').startOf('week');
                let endDate = moment(calendarApi.getDate()).add(1, 'weeks').endOf('week');

                setDateRange({
                    startDate: startDate.format(),
                    endDate: endDate.format(),
                });

                setCurrentDate(startDate.format());

                calendarApi.next();
            }
        }
    }

    const onDayBtnClick = (day) => {
        if (calendarRef && calendarRef.current && calendarRef.current.getApi()) {

            setCurrentDate(day);
            let calendarApi = calendarRef.current.getApi();

            if (selectedView === 'Jour') {
                let startDate = moment(day).startOf('day').format();
                let endDate = moment(day).endOf('day').format();

                setDateRange({
                    startDate: startDate,
                    endDate: endDate,
                });
            } else if (selectedView === 'Semaine') {
                let weekStart = moment(day).startOf('week');
                let startDate = weekStart.format();
                let endDate = moment(weekStart).endOf('week').format();

                setDateRange({
                    startDate: startDate,
                    endDate: endDate,
                });
            }

            calendarApi.gotoDate(day);
        }
    }

    const changeViewMode = () => {
        if (calendarRef && calendarRef.current && calendarRef.current.getApi()) {

            let calendarApi = calendarRef.current.getApi();
            let currentView = calendarApi.view.type;
            let newView = currentView === 'timeGridWeek' ? 'timeGridDay' : 'timeGridWeek';
            setSelectedView(newView === 'timeGridWeek' ? 'Semaine' : 'Jour');

            if (newView === 'timeGridWeek') {
                let startDate = moment(calendarApi.getDate()).startOf('week');
                let endDate = moment(calendarApi.getDate()).endOf('week');

                setDateRange({
                    startDate: startDate.format(),
                    endDate: endDate.format(),
                });
            } else {
                let dayFormat = moment();
                let startDate = dayFormat.startOf('day').format();
                let endDate = dayFormat.endOf('day').format();

                setDateRange({
                    startDate: startDate,
                    endDate: endDate
                });

                setCurrentDate(startDate);
            }

            calendarApi.changeView(newView);
        }
    }

    const onCalendarEventClick = (info) => {
        setSelectedPlanification(info.event._def.extendedProps.planification);
        setPlanificationMode(info.event._def.extendedProps.planification.type);
        setOpenModal(true);
    };
    const onCalendarEventChange = (info) => {
        let planification = info.event._def.extendedProps.planification;

        let dateRange = info.event._instance.range;

        let timeZoneOffset = dateRange.start.getTimezoneOffset();

        let datePassage = {
            startDate: moment(dateRange.start).subtract(Math.abs(timeZoneOffset), 'minutes').format(),
            endDate: moment(dateRange.end).subtract(Math.abs(timeZoneOffset), 'minutes').format(),
        };

        updateRequestPassDatePassage(planification.id, datePassage);
    };

    const goToDate = (planificationDate) => {
        setTab(PLANIFICATION_TYPE.PLANNING);

        let startDate = moment(planificationDate).startOf('week');
        let endDate = moment(planificationDate).endOf('week');

        setDateRange({
            startDate: startDate.format(),
            endDate: endDate.format(),
        });

        setTimeout(() => {
            let calendarApi = calendarRef.current.getApi();
            calendarApi.gotoDate(planificationDate);
        }, 1000);

        setOpenModal(false);
    };

    const handleTabChange = (e) => {
        setTab(e);
    };


    //#endregion

    //#region API

    const apiGetPlanifications = () => {
        if (tab === PLANIFICATION_TYPE.PLANNING) {
            if (profil.profil === 1 || profil.profil === 2 || profil.profil === 3) {
                axios({
                    method: 'post', url: URL_API + '/planification/list-by-dates', params: {
                        startDate: dateRange.startDate.split('+')[0],
                        endDate: dateRange.endDate.split('+')[0],
                        requestPass: requestPassId,
                    },
                    data: { hubs: profil.profil === 3 ? selectedValue.centerSelected.length >= 1 ? selectedValue.centerSelected : centersAffected.map((center) => center.id) : selectedValue.centerSelected },
                })
                .then((response) => {
                    if (response.data) {
                        setCalendarEvents(convertPlanificationsToCalendarEvent(response.data));
                    }

                    setIsLoading(false);
                    setPageFullyLoaded(true);
                })
                .catch((error) => {
                    setIsLoading(false);
                    logError(alert, error);
                });
            } else {
                axios({
                    method: 'post',
                    url: URL_API + '/planification/list-by-client-and-dates',
                    params: {
                        startDate: dateRange.startDate.split('+')[0],
                        endDate: dateRange.endDate.split('+')[0],
                        client: user.raison_sociale,
                        requestPass: requestPassId,
                    },
                    data: { hubs: selectedValue.centerSelected || null },
                })
                .then((response) => {
                    if (response.data) {
                        setCalendarEvents(convertPlanificationsToCalendarEvent(response.data));
                    }

                    setIsLoading(false);
                    setPageFullyLoaded(true);
                })
                .catch((error) => {
                    setIsLoading(false);
                    logError(alert, error);
                });
            }
        } else {
            if (profil.profil === 1 || profil.profil === 2 || profil.profil === 3) {
                axios({
                    method: 'post',
                    url: URL_API + '/planification/list',
                    params: {
                        requestPass: requestPassId,
                    },
                    data: { hubs: profil.profil === 3 ? selectedValue.centerSelected.length >= 1 ? selectedValue.centerSelected : centersAffected.map((center) => center.id) : selectedValue.centerSelected },
                })
                .then((response) => {
                    if (response.data) {
                        setPlanifications(response.data);
                    }
                    setIsLoading(false);
                })
                .catch((error) => {
                    setIsLoading(false);
                    logError(alert, error);
                });
            } else {
                axios({
                    method: 'post',
                    url: URL_API + '/planification/list-by-client',
                    params: {
                        client: user.raison_sociale,
                        requestPass: requestPassId,
                    },
                    data: { hubs: selectedValue.centerSelected || null },
                })
                .then((response) => {
                    if (response.data) {
                        setPlanifications(response.data);
                    }
                    setIsLoading(false);
                })
                .catch((error) => {
                    setIsLoading(false);
                    logError(alert, error);
                });
            }
        }
    };
    const apiGetCentersAffectedToUser = () => {
        axios({ method: 'get', url: `${URL_API}/localisation/centers-by-user`, params: { userId: user.user_id } })
        .then(function(response) {
            centersAffectedRef.current = response.data;
            setCentersAffected(response.data);
            setIsLoading(false);
        })
        .catch(function(err) {
            logError(alert, err);
        });
    };
    const apiGetListCenters = () => {
        axios({ method: 'get', url: `${URL_API}/localisation/list-all-centers` })
        .then(function(response) {
            listCentersRef.current = response.data;
            setListCenters(response.data);
            setIsLoading(false);
        })
        .catch(function(err) {
            logError(alert, err);
        });
    };
    const updateRequestPassDatePassage = (planificationId, datePassage) => {
        axios({
            method: 'POST',
            url: `${URL_API}/planification/update-date-passage`,
            data: datePassage,
            params: { planificationId: planificationId },
        })
        .then(() => {
            alert.removeAll();
            alert.success('La date de passage a été mise à jour !');
        })
        .catch((error) => {
            alert.removeAll();
            alert.error(error.message);
        });
    };

    //#endregion

    return (
        <div style={{ height: 'calc(100% - 30px)' }}>
            <TopBar title={'Planification des passages'}
                    className='topbar-contenu planification topbar-contenu-rose'
                    color={props.params.color}
                    height={'80px'}
                    buttons={
                        <div className='topbar-buttons'>
                            {(profil.profil === 1 || profil.profil === 2 || profil.profil === 3) &&
                                <MyButton onClick={() => {
                                    onCreatePeriodModalBtnClick(PLANIFICATION_STATUS.UNAVAILABLE);
                                }}
                                          style={'buttonDefault'}
                                          icon={faPlus}
                                          label={`indisponibilité`}
                                          sx={{backgroundColor: '#7f2352c2 !important'}}
                                />
                            }
                            <MyButton onClick={() => {
                                onCreatePeriodModalBtnClick(PLANIFICATION_STATUS.AVAILABLE);
                            }}
                                      style={'buttonDefault'}
                                      icon={faPlus}
                                      label={'Période de passage'}
                                      sx={{backgroundColor: '#7f2352c2 !important'}}
                            />
                            {tab && tab === PLANIFICATION_TYPE.PLANNING &&
                                <>
                                    <MyButton onClick={onPrevWeekBtnClick} style={'buttonIcon'} icon={faCaretLeft} sx={{backgroundColor: '#7f2352c2 !important'}}
                                              iconOnly={true} label={''} />

                                    <DatePicker
                                            name='startDate'
                                            value={currentDate}
                                            renderInput={(params) => <TextField {...params} sx={{
                                                    backgroundColor: '#7f2352c2',
                                                    borderRadius: '5px',
                                                    color: '#ffffff !important',
                                                    margin: '10px',
                                                    fontWeight: 'bold',
                                                    minHeight: '35px',
                                                    maxWidth: '180px',
                                                    '.MuiInputBase-root': {
                                                        color: '#ffffff !important'
                                                    },
                                                    '.MuiButtonBase-root': {
                                                        color: '#ffffff !important'
                                                    }
                                            }} />}
                                            onChange={(date) => onDayBtnClick(date)}
                                    />
                                    <MyButton onClick={onNextWeekBtnClick} style={'buttonIcon'} icon={faCaretRight} sx={{backgroundColor: '#7f2352c2 !important'}}
                                              iconOnly={true} label={''} />

                                    <MyButton onClick={onTodayBtnClick} style={'buttonDefault'} icon={''} label={'Aujourd\'hui'} sx={{backgroundColor: '#7f2352c2 !important'}}/>

                                    <MyButton onClick={changeViewMode} style={'buttonDefault'} icon={''} label={selectedView === 'Semaine' ? 'Jour' : 'Semaine'} sx={{backgroundColor: '#7f2352c2 !important'}}/>
                                </>
                            }
                        </div>
                    }
                    filterContainer={
                        <>
                            <MySelect name={'centerSelected'} label={'Centre'} displayLabel={true}
                                    style={{width: '200px'}}
                                    multiple={true} value={selectedValue.centerSelected}
                                    sx={{backgroundColor: '#7f2352c2 !important', color: 'white !important'}}
                                    datas={centersAffected}
                                    labelClass={'select-label'}
                                    handleChange={changeSelectValue} />
                        </>
                    }
                    radioSelector={
                        <SelectorTypeList leftValue={PLANIFICATION_TYPE.PLANNING} rightValue={PLANIFICATION_TYPE.LIST}
                                          onChange={(e) => handleTabChange(e)} />
                    }
            />
            <div className='row' style={{ margin: 0 }}>
                <ClipLoader color={props.params.color} loading={isLoading} size={75}
                            css={'display: flex; margin: 25px;justify-content: center;'} />
                {!isLoading && (
                    <div style={{ width: '100%' }}>

                        {tab && tab === PLANIFICATION_TYPE.PLANNING &&
                            <FullCalendar
                                ref={calendarRef}
                                headerToolbar={false}
                                events={calendarEvents}
                                plugins={[timeGridPlugin, interactionPlugin]}
                                initialView={selectedView === 'Semaine' ? 'timeGridWeek' : 'timeGridDay'}
                                allDaySlot={false}
                                nowIndicator={true}
                                locales={[frLocale]}
                                initialDate={dateRange.startDate}
                                rerenderDelay={10}
                                height={window.innerHeight - 120}
                                eventMinHeight={window.innerHeight / 6}
                                eventContent={(info) => {
                                    if (info.event.extendedProps.planification.type === PLANIFICATION_STATUS.UNAVAILABLE) {
                                        info.backgroundColor = '#f2f4f7';
                                    } else {
                                        info.backgroundColor = '#ffffff';
                                    }

                                    let planification = info.event.extendedProps.planification;

                                    return <PlanificationEvent
                                        planification={planification}
                                        eventInfo={info}
                                        centers={listCenters}
                                    />;
                                }}
                                eventDrop={onCalendarEventChange}
                                eventResize={onCalendarEventChange}
                                eventClick={onCalendarEventClick}
                                eventMouseEnter={(mouseEnterInfo) => {
                                    mouseEnterInfo.el.parentElement.style.height = window.innerHeight - 300 + 'px';
                                }}
                                eventMouseLeave={(mouseEnterInfo) => {
                                    mouseEnterInfo.el.parentElement.style.height = 'auto';
                                }}
                                slotDuration='01:00:00'
                                dayHeaderFormat={{ weekday: 'short', month: 'numeric', day: 'numeric', omitCommas: true }}
                            />}

                        {tab && tab === PLANIFICATION_TYPE.LIST &&
                            <AgGrid
                                gridName='ag-grid-planification'
                                rowData={planifications}
                                columnDefs={columns}
                                rowHeight={40}
                                pagination={false}
                                frameworkComponents={frameworkComponents}
                                gridOptions={agGridOptions}
                                setGridOptions={setAgGridOptions}
                                sortable={false}
                                filters={filters}
                                color='pink'
                            />
                        }
                    </div>
                )}
                <EditPlanificationModal
                    mode={planificationMode}
                    openModal={openModal}
                    centers={listCenters}
                    setReloadDatas={setReloadDatas}
                    setOpenModal={setOpenModal}
                    planification={selectedPlanification}
                    setPlanification={setSelectedPlanification}
                    client={client}
                    requestPassId={requestPassId}
                    goToDate={goToDate}
                />
            </div>
        </div>);
};

export default PlanificationViewer;
